import { FC } from "react";
import { Control, useFieldArray } from "react-hook-form";
import styled, { useTheme } from "styled-components";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { FormDataT } from "./DistributionRepostsForm";
import { FormLayout, Input, Label, Larger, Textarea } from "@/components/Forms";
import { api } from "@/utils/api";
import { ChannelResource } from "@/types/resources";
import FormField from "@/components/Forms/FormField";
import Text from "@/components/Text";
import { compilationTypes } from "./DistributionScheduleModal";

const Repost = styled(FormLayout)`
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding: 24px 0;
  &:first-child {
    border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const RemoveButton = styled(Text).attrs({
  $type: "smallHeadline",
  $color: "error",
  type: "button",
  as: "button",
})`
  position: absolute;
  right: 0;
  top: 24px;
  &:hover {
    text-decoration: underline;
  }
`;

const CountBadge = styled(Text)`
  position: absolute;
  top: 24px;
  left: 104px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.purpleLighter};
`;

const AddButton = styled(Text)`
  display: inline-flex;
  align-items: center;
  margin-top: 20px;
  svg {
    margin-right: 10px;
  }
`;

interface Props {
  control: Control<FormDataT>;
  conditionsIndex: number;
  title: string;
  description: string;
}

const RepostsFieldsArray: FC<Props> = ({
  control,
  conditionsIndex,
  title,
  description,
}) => {
  const { teamId } = useParams();

  const { data: channelOptions } = useQuery(
    ["channels", { teamId, onlyBp: true, authorized: true }],
    () =>
      api.get<never, ChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          ...(teamId !== "all" && {
            team_id: teamId,
          }),
          only_bp: 1,
        },
      }),
    {
      staleTime: 1000 * 60,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    }
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: `conditions.${conditionsIndex}.reposts`,
  });

  const theme = useTheme();

  return (
    <div>
      {fields.map((field, index) => (
        <Repost key={field.id}>
          {fields.length > 1 && (
            <RemoveButton onClick={() => remove(index)}>Remove</RemoveButton>
          )}
          <CountBadge as="span" $type="extraSmall" $color="grayscale">
            {index + 1}
          </CountBadge>
          <FormField<FormDataT, number>
            control={control}
            options={channelOptions ?? []}
            label="Posting Channel"
            type="select"
            name={`conditions.${conditionsIndex}.reposts.${index}.channel_id`}
          />
          <FormField<FormDataT, number>
            control={control}
            label="Date & Time"
            type="datepicker"
            name={`conditions.${conditionsIndex}.reposts.${index}.date`}
          />
          <Larger>
            <Label>Title</Label>
            <Input defaultValue={title} readOnly />
          </Larger>
          <Larger>
            <Label>Description</Label>
            <Textarea defaultValue={description} readOnly />
          </Larger>
          <FormField<FormDataT, number>
            control={control}
            options={compilationTypes}
            label="Compilation Type"
            type="select"
            name={`conditions.${conditionsIndex}.reposts.${index}.compilation_type`}
          />
        </Repost>
      ))}
      <AddButton
        as="button"
        type="button"
        $type="smallHeadline"
        onClick={() => append({})}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="8"
            cy="8"
            r="8"
            fill={theme.colors.purple}
            fillOpacity="0.12"
          />
          <path
            d="M8 5V11"
            stroke={theme.colors.purple}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5 8H11"
            stroke={theme.colors.purple}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Add additional posting channel
      </AddButton>
    </div>
  );
};

export default RepostsFieldsArray;
